import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

// Check before instantiating any Firebase service
const configured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

export const isFirebaseConfigured = () => configured;

// Only initialize when credentials are present — prevents auth/invalid-api-key
// error at module load time, allowing the app to run in demo mode
let app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;

if (configured) {
  app = initializeApp(firebaseConfig);
  _auth = getAuth(app);
  _db = getFirestore(app);

  // Firebase App Check — validates requests come from this app, not scripts/tools.
  // Uses reCAPTCHA Enterprise (invisible). Set the site key in your env.
  // In development, enable the debug provider via VITE_APPCHECK_DEBUG_TOKEN=true
  const appCheckKey = import.meta.env.VITE_FIREBASE_APPCHECK_KEY;
  if (appCheckKey) {
    if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
      // Debug token for local development — allows emulator/localhost to pass App Check
      (self as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN === 'true' ? true : import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
    }
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckKey),
      isTokenAutoRefreshEnabled: true,
    });
  }
}

export const auth = _auth as Auth;
export const db = _db as Firestore;

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });
